/**
 * PriorityRoadmap projection: what to fix next, and what each fix recovers.
 *
 * Everything here is arithmetic over a rubric the tools already computed. There
 * is no forecast, no decay curve, and no claim that infrastructure work moves
 * citation rates. Four rules keep it honest:
 *
 *  1. NON-GRADEABLE TOOLS ARE EXCLUDED ENTIRELY. A tool that could not measure
 *     the site contributes no baseline, no ceiling, and no quick win. A 0 from
 *     "we couldn't reach your server" is not a floor to improve from.
 *
 *  2. POINTS ARE ATTRIBUTED PER ACTION, NOT PER CATEGORY. Several actions can
 *     target one category, so a category's remaining points are DIVIDED among
 *     them. Their sum can never exceed what the category has left — the
 *     ceiling invariant, asserted in projection.test.ts.
 *
 *  3. A SHARED ACTION IS COUNTED ONCE. One fix can move two tools (creating
 *     llms.txt moves both crawl and llms-validator). Attribution is stored as
 *     per-tool entries and only summed for display, so a 20+24 action can
 *     never become 44 on both tools' subtotals.
 *
 *  4. GATED POINTS ARE NOT REACHABLE TODAY. Where an artifact does not exist,
 *     only the minimal-valid baseline is recoverable now; the quality points
 *     behind it are reported separately and never folded into the actionable
 *     ceiling.
 *
 * ATTRIBUTION MODEL (be precise about what this is): the within-category split
 * is proportional to each action's `rubricWeight`, defaulting to an equal
 * share. It is a documented apportionment, NOT a measurement — the rubric
 * exposes points per category, not per sub-item, so we cannot know that
 * unblocking GPTBot specifically is worth exactly 3 points. What IS guaranteed
 * is that the parts never sum to more than the whole.
 */

import type {
  ActionItem,
  ActionSource,
  RubricRef,
  ToolKey,
} from '@/types/radar'
import type { TierOneKey } from '@/lib/platform/derive-tool-scores'
import { MINIMAL_VALID_BASELINE } from './rubric-baselines'
import { strandedReason } from './stranded-reasons'
import { compareActions } from './action-priority'
import {
  checkToolParity,
  PARITY_TOOLS,
  type ParityCheck,
  type ParityFailureKind,
  type RubricCategory,
} from './parity'

export interface ToolProjection {
  tool: TierOneKey
  /** Today's score, 0-100, as the tool reports it. */
  current: number
  /**
   * Every rubric point earned. Always 100 for a plain-sum tool; for a
   * normalized tool (crawl-check) it is 100 across the VERIFIABLE slice,
   * because unverifiable categories cannot be fixed by shipping anything.
   */
  theoreticalCeiling: number
  /** current + everything the known actions can actually recover TODAY. */
  actionableCeiling: number
  /** current + the low-effort subset of those actions. */
  quickWinCeiling: number
  /**
   * Points locked behind a foundational fix. Real, but not reachable until the
   * artifact exists. Never part of actionableCeiling.
   */
  gatedPoints: number
  /**
   * Points that exist in the rubric with no action pointing at them and no
   * gate explaining why. Surfaced, never folded into a quick win.
   */
  unattributedPoints: number
}

/**
 * One tool's share of an action's recovery.
 *
 * UNROUNDED on purpose. Rounding at insertion and then summing the rounded
 * parts breaks the ceiling invariant: 11 equal actions splitting a 1-point gap
 * each round 0.0909 up to 0.1, and the parts sum to 1.1 of a 1-point category.
 * Rounding is a presentation concern and happens only at the display edge.
 */
export interface ActionAttribution {
  tool: TierOneKey
  category: string
  rubricPoints: number
  scorePoints: number
}

export interface RecoverableAction {
  actionId: string
  title: string
  effort: ActionItem['effort']
  impact: ActionItem['impact']
  /** Carried from the source ActionItem so the canonical comparator can
   *  order these identically to every other action surface. */
  priorityDetail?: ActionItem['priorityDetail']
  /** Per-tool shares, one entry per CATEGORY touched. */
  attributions: ActionAttribution[]
  /**
   * Attributions collapsed to one entry per TOOL — what the UI shows
   * ("+20 Crawl Check, +24 llms.txt Validator"). Listing raw categories there
   * would read as "+17 llms, +7 llms" for a fix that recovers 24.
   */
  perTool: { tool: TierOneKey; scorePoints: number }[]
  /** Sum across tools — for RANKING and display only, never for a subtotal. */
  scorePoints: number
  /**
   * The same recovery expressed on the AGGREGATE infrastructure-score scale,
   * so a fix can be read against the header and the gap bar.
   *
   * `scorePoints` is raw per-tool rubric points; the infrastructure score is a
   * MEAN over the gradeable tools. A +47 fix therefore moves the aggregate by
   * ~47/6, not 47. Without this the table and the header look like they
   * contradict each other.
   *
   *   scoreDelta = scorePoints / gradeableToolCount
   *
   * Shared fixes sum their per-tool parts first (already folded into
   * scorePoints), so one fix touching two tools is divided once, not twice.
   */
  scoreDelta: number
}

export interface UnfixedCategory {
  tool: TierOneKey
  category: string
  remainingPoints: number
  /**
   * Present when we triaged the category and DECIDED not to ship advice —
   * score-chasing, or re-derived from another tool. Absent means genuinely
   * not yet triaged, which the UI must word differently.
   */
  reason?: string
}

/** Points that become reachable only after a foundational fix ships. */
export interface GatedCategory {
  tool: TierOneKey
  category: string
  points: number
  unlockedByActionId: string
  unlockedByTitle: string
}

/** An action we cannot price. Never dropped, never rendered blank. */
export interface UnattributedAction {
  actionId: string
  title: string
  /**
   * ActionSource, not ToolKey: a non-scoring producer such as a GSC join
   * check has no rubric category by design and lands here. Widening only
   * this field keeps ToolKey meaning "a scored Radar tool" everywhere else.
   */
  tool: ActionSource
  reason:
    | 'no-rubric-ref'
    | 'tool-not-gradeable'
    | 'category-not-found'
    | 'category-already-full'
}

export interface Projection {
  ok: boolean
  excluded: { tool: TierOneKey; kind: ParityFailureKind; reason: string }[]
  tools: ToolProjection[]
  /**
   * Mean across GRADEABLE infrastructure tools only. Deliberately NOT called
   * `unifiedScore`: the dashboard's unified score averages every completed
   * tool including monitoring ones, so these two numbers differ by
   * construction and must never be presented as the same figure. The UI must
   * label this visibly as an infrastructure sub-score over 6 tools.
   */
  infrastructureScore: number
  infrastructureCeiling: number
  infrastructureQuickWin: number
  /** Denominator behind every aggregate figure and every scoreDelta. */
  gradeableToolCount: number
  rankedActions: RecoverableAction[]
  topAction: RecoverableAction | null
  unfixedCategories: UnfixedCategory[]
  gatedCategories: GatedCategory[]
  unattributedActions: UnattributedAction[]
  /** Sum of every action's recovery. A conservative FLOOR, not a cap. */
  totalRecoverablePoints: number
  /** Sum of points waiting on a foundational fix. */
  totalGatedPoints: number
  /** Sum of points we triaged and chose not to route into advice. */
  totalStrandedPoints: number
}

function isTierOne(tool: ToolKey): tool is TierOneKey {
  return (PARITY_TOOLS as readonly string[]).includes(tool)
}

function remainingPoints(c: RubricCategory): number {
  if (!c.verifiable) return 0
  return Math.max(0, c.maxScore - c.score)
}

function scoreLeverage(categories: RubricCategory[]): number {
  const measurable = categories
    .filter(c => c.verifiable)
    .reduce((sum, c) => sum + c.maxScore, 0)
  return measurable > 0 ? 100 / measurable : 0
}

function round1(n: number): number {
  return Math.round(n * 10) / 10
}

export function buildProjection(
  results: Partial<Record<TierOneKey, unknown>>,
  actions: ActionItem[]
): Projection {
  // ── 1. Gradeability ────────────────────────────────────────────────────
  const checks = new Map<TierOneKey, ParityCheck>()
  const excluded: Projection['excluded'] = []
  for (const tool of PARITY_TOOLS) {
    const check = checkToolParity(
      tool,
      results[tool] as Parameters<typeof checkToolParity>[1]
    )
    checks.set(tool, check)
    if (!check.ok) {
      excluded.push({
        tool,
        kind: check.failureKind ?? 'missing',
        reason: check.reason ?? 'unknown',
      })
    }
  }
  const gradeable = PARITY_TOOLS.filter(t => checks.get(t)?.ok)

  const categoryOf = (tool: TierOneKey, name: string) =>
    checks.get(tool)?.categories.find(c => c.category === name)

  // ── 2. Group actions by the category they point at ─────────────────────
  const byCategory = new Map<string, ActionItem[]>()
  const unattributedActions: UnattributedAction[] = []
  const key = (tool: string, category: string) => `${tool}::${category}`

  // An action may point at several categories (one edit that two tools each
  // measure, or a fix with a real side effect elsewhere). Each ref is joined
  // independently; the action is only reported unattributed when NO ref lands.
  const refCap = new Map<string, number>()
  for (const action of actions) {
    const raw = action.rubricRef
    const refs: RubricRef[] = !raw ? [] : Array.isArray(raw) ? raw : [raw]
    if (refs.length === 0) {
      unattributedActions.push({
        actionId: action.id,
        title: action.title,
        tool: action.source,
        reason: 'no-rubric-ref',
      })
      continue
    }

    let landed = 0
    let lastReason: UnattributedAction['reason'] = 'no-rubric-ref'
    for (const ref of refs) {
      if (!isTierOne(ref.tool) || !checks.get(ref.tool)?.ok) {
        lastReason = 'tool-not-gradeable'
        continue
      }
      const category = categoryOf(ref.tool, ref.category)
      if (!category) {
        lastReason = 'category-not-found'
        continue
      }
      if (remainingPoints(category) === 0) {
        lastReason = 'category-already-full'
        continue
      }
      const k = key(ref.tool, ref.category)
      byCategory.set(k, [...(byCategory.get(k) || []), action])
      if (ref.maxClaim !== undefined) {
        refCap.set(`${action.id}::${k}`, ref.maxClaim)
      }
      landed++
    }

    if (landed === 0) {
      unattributedActions.push({
        actionId: action.id,
        title: action.title,
        tool: action.source,
        reason: lastReason,
      })
    }
  }

  // ── 3. Attribute points, PER TOOL ──────────────────────────────────────
  // Every entry names exactly one tool. A shared action produces two entries,
  // never one entry counted twice.
  const attributionsByAction = new Map<string, ActionAttribution[]>()
  const actionById = new Map<string, ActionItem>()
  const addAttribution = (action: ActionItem, a: ActionAttribution) => {
    actionById.set(action.id, action)
    attributionsByAction.set(action.id, [
      ...(attributionsByAction.get(action.id) || []),
      a,
    ])
  }

  for (const [k, group] of byCategory) {
    const [toolKey, categoryName] = k.split('::') as [TierOneKey, string]
    const check = checks.get(toolKey)!
    const category = categoryOf(toolKey, categoryName)!
    const remaining = remainingPoints(category)
    const leverage = scoreLeverage(check.categories)
    const totalWeight = group.reduce(
      (sum, a) => sum + Math.max(1, a.rubricWeight ?? 1),
      0
    )

    // The category's remaining points are a BUDGET that allocation draws
    // down. Every allocation is clamped to what is actually left, so the
    // parts can never sum past the whole — not by a cap interaction, and not
    // by float error accumulating across many small shares. Conservation is
    // structural here rather than something a tolerance has to absorb.
    let budget = remaining

    for (const action of group) {
      const weight = Math.max(1, action.rubricWeight ?? 1)
      // A capped ref claims only its real side effect, never the whole gap.
      const cap = refCap.get(`${action.id}::${k}`)
      const share = (remaining * weight) / totalWeight
      const rubricPoints = Math.max(0, Math.min(share, cap ?? Infinity, budget))
      budget -= rubricPoints
      addAttribution(action, {
        tool: toolKey,
        category: categoryName,
        rubricPoints,
        scorePoints: rubricPoints * leverage,
      })
    }
  }

  // ── 4. Foundational fixes: baseline now, the rest gated ────────────────
  const gatedCategories: GatedCategory[] = []
  const gatedTools = new Set<TierOneKey>()

  for (const action of actions) {
    const foundational = action.rubricFoundational
    if (!foundational) continue
    const tool = foundational.tool
    if (!isTierOne(tool) || !checks.get(tool)?.ok) continue

    const baseline = MINIMAL_VALID_BASELINE[tool]
    if (!baseline) continue

    const check = checks.get(tool)!
    const leverage = scoreLeverage(check.categories)
    gatedTools.add(tool)

    for (const category of check.categories) {
      const remaining = remainingPoints(category)
      if (remaining === 0) continue
      // Only the minimal-valid share is recoverable now, and never more than
      // the category actually has left.
      const claimable = Math.min(baseline[category.category] ?? 0, remaining)
      if (claimable > 0) {
        addAttribution(action, {
          tool,
          category: category.category,
          rubricPoints: claimable,
          scorePoints: claimable * leverage,
        })
      }
      const gated = remaining - claimable
      if (gated > 0) {
        gatedCategories.push({
          tool,
          category: category.category,
          points: gated * leverage,
          unlockedByActionId: action.id,
          unlockedByTitle: action.title,
        })
      }
    }
  }
  gatedCategories.sort((a, b) => b.points - a.points)

  // ── 5. Collapse to one row per action ──────────────────────────────────
  // The aggregate is a mean over gradeable tools, so that count is the
  // divisor that puts a fix on the same scale as the header.
  const gradeableToolCount = gradeable.length
  const rankedActions: RecoverableAction[] = []
  for (const [actionId, attributions] of attributionsByAction) {
    const action = actionById.get(actionId)!
    const perToolMap = new Map<TierOneKey, number>()
    for (const at of attributions) {
      perToolMap.set(at.tool, (perToolMap.get(at.tool) ?? 0) + at.scorePoints)
    }
    rankedActions.push({
      actionId,
      title: action.title,
      effort: action.effort,
      impact: action.impact,
      priorityDetail: action.priorityDetail,
      attributions,
      perTool: [...perToolMap].map(([tool, scorePoints]) => ({
        tool,
        scorePoints: round1(scorePoints),
      })),
      scorePoints: round1(
        attributions.reduce((sum, a) => sum + a.scorePoints, 0)
      ),
      scoreDelta:
        gradeableToolCount > 0
          ? round1(
              attributions.reduce((sum, a) => sum + a.scorePoints, 0) /
                gradeableToolCount
            )
          : 0,
    })
  }

  // Canonical order — the SAME comparator the actions panel, export and
  // webhook use, so the roadmap can no longer disagree with them about what
  // to do first. This replaces a local scorePoints-then-effort sort.
  //
  // Deliberate consequence: rubric scorePoints no longer drives ORDER. It is
  // still computed, still displayed, and the ceiling invariant is untouched —
  // but "recovers the most points" and "do this first" are different
  // questions, and only one order can be canonical.
  rankedActions.sort((a, b) =>
    compareActions(
      {
        id: a.actionId,
        effort: a.effort,
        impact: a.impact,
        priorityDetail: a.priorityDetail,
      },
      {
        id: b.actionId,
        effort: b.effort,
        impact: b.impact,
        priorityDetail: b.priorityDetail,
      }
    )
  )

  // ── 6. Per-tool projections ────────────────────────────────────────────
  // Subtotals read the PER-TOOL attributions, never RecoverableAction
  // .scorePoints, so a shared action contributes only its own share here.
  const tools: ToolProjection[] = gradeable.map(tool => {
    const check = checks.get(tool)!
    const current = (results[tool] as { overallScore: number }).overallScore

    let actionable = 0
    let quickWin = 0
    for (const ra of rankedActions) {
      for (const at of ra.attributions) {
        if (at.tool !== tool) continue
        actionable += at.scorePoints
        if (ra.effort === 'low') quickWin += at.scorePoints
      }
    }

    const leverage = scoreLeverage(check.categories)
    const totalRemaining = check.categories.reduce(
      (sum, c) => sum + remainingPoints(c),
      0
    )
    const gated = gatedCategories
      .filter(g => g.tool === tool)
      .reduce((sum, g) => sum + g.points, 0)

    return {
      tool,
      current,
      theoreticalCeiling: round1(current + totalRemaining * leverage),
      actionableCeiling: round1(current + actionable),
      quickWinCeiling: round1(current + quickWin),
      gatedPoints: round1(gated),
      // Clamped: attribution now runs at full precision, but float error can
      // still leave a residue like -1e-14, and a negative "points with no fix"
      // is nonsense on screen.
      unattributedPoints: round1(
        Math.max(0, totalRemaining * leverage - actionable - gated)
      ),
    }
  })

  // ── 7. Categories nobody can act on ────────────────────────────────────
  // A gated tool is excluded here: its points are not unknowable, they are
  // waiting on a named fix, and are reported as gatedCategories instead.
  const unfixedCategories: UnfixedCategory[] = []
  for (const tool of gradeable) {
    if (gatedTools.has(tool)) continue
    for (const c of checks.get(tool)!.categories) {
      const remaining = remainingPoints(c)
      const attributedHere = rankedActions
        .flatMap(a => a.attributions)
        .filter(at => at.tool === tool && at.category === c.category)
        .reduce((sum, at) => sum + at.rubricPoints, 0)
      // Same clamp, and a sub-0.05 residue rounds to 0 and is not a real
      // blind spot worth listing.
      const leftover = round1(Math.max(0, remaining - attributedHere))
      if (leftover > 0) {
        unfixedCategories.push({
          tool,
          category: c.category,
          remainingPoints: leftover,
          reason: strandedReason(tool, c.category),
        })
      }
    }
  }
  unfixedCategories.sort((a, b) => b.remainingPoints - a.remainingPoints)

  const mean = (nums: number[]) =>
    nums.length ? round1(nums.reduce((s, n) => s + n, 0) / nums.length) : 0

  return {
    ok: excluded.length === 0,
    excluded,
    tools,
    infrastructureScore: mean(tools.map(t => t.current)),
    infrastructureCeiling: mean(tools.map(t => t.actionableCeiling)),
    infrastructureQuickWin: mean(tools.map(t => t.quickWinCeiling)),
    gradeableToolCount,
    rankedActions,
    // Biggest ABSOLUTE recovery, computed independently of array order.
    // It used to be rankedActions[0], which only worked while that array was
    // sorted by scorePoints. Now that rankedActions carries the canonical
    // execution order, "the biggest recovery" has to be asked for directly
    // or the hero would silently become "the top of whatever order we chose".
    topAction:
      rankedActions.length > 0
        ? rankedActions.reduce((best, a) =>
            a.scorePoints > best.scorePoints ? a : best
          )
        : null,
    unfixedCategories,
    gatedCategories,
    unattributedActions,
    totalRecoverablePoints: round1(
      rankedActions.reduce((sum, a) => sum + a.scorePoints, 0)
    ),
    totalGatedPoints: round1(
      gatedCategories.reduce((sum, g) => sum + g.points, 0)
    ),
    totalStrandedPoints: round1(
      unfixedCategories.reduce((sum, u) => sum + u.remainingPoints, 0)
    ),
  }
}
